import { useCallback, useEffect, useRef, useState } from 'react'
import { EonUiHeadline, EonUiLink, EonUiPreloaderCircle } from '@eon-ui/eon-ui-components-react'
import { Icon } from '@me8eon/icons'
import {
  ACTIVITIES_PANEL_OPEN_EVENT,
  consumePendingActivitiesPanelOpen,
} from '@me8eon/new_ai_utils/src/config/chat.config'
import { useNexthinkActionTracker } from '@me8eon/new_ai_utils/src/contexts/NexthinkActionTrackerProvider'
import { useActivitySidebarItems } from '@me8eon/new_ai_utils/src/hooks/useActivitySidebarItems'
import type { SidebarTaskItem, SidebarTicketItem } from '@me8eon/new_ai_utils/src/utils/ticketSidebarItems'
import { useTranslation } from '@me8eon/translation'
import { formatDateTimeWithoutSeconds } from '@me8eon/time'
import styles from './RightSidebar.module.scss'

function TicketCard({ item }: { item: SidebarTicketItem }) {
  const t = useTranslation()
  return (
    <div className={styles.rightSidebar__card}>
      <div className={styles.rightSidebar__cardHeader}>
        <span className={styles.rightSidebar__statusTag}>{item.status}</span>
        <div className={styles.rightSidebar__cardLine}>
          <Icon name="contract" size="normal" />
          <span>
            {t('chatBot.rightSidebar.ticketPrefix')} <strong>{item.ticketNumber}</strong>
          </span>
        </div>
        {item.openedAt ? (
          <div className={styles.rightSidebar__cardMeta}>
            {t('chatBot.rightSidebar.openedAtPrefix')} {formatDateTimeWithoutSeconds(item.openedAt)}
          </div>
        ) : null}
      </div>
      <p className={styles.rightSidebar__cardDescription}>{item.description}</p>
      <div className={styles.rightSidebar__cardLink}>
        <EonUiLink text={t('chatBot.rightSidebar.showTicketDetails')} size="small" scheme="neutral800" target="_blank" href={"/incidents"} icon-position="left" />
      </div>
    </div>
  )
}

function TaskRow({ item }: { item: SidebarTaskItem }) {
  return (
    <div className={styles.rightSidebar__task}>
      {item.status === 'success' ? (
        <Icon size="small" name="tick_small" scheme="red500" />
      ) : item.status === 'failure' ? (
        <Icon size="small" name="task_failed" />
      ) : (
        <EonUiPreloaderCircle size="small" />
      )}
      <span>{item.label}</span>
    </div>
  )
}

export interface RightSidebarProps {
  open?: boolean
  onOpenChange?: (open: boolean) => void
  onActivityChange?: (hasActivity: boolean) => void
}

/**
 * The authenticated app’s right-hand "Activities in progress" panel (tickets and tasks).
 * Opens on a confirmed ticket creation, a triggered Nexthink action, or
 * `ACTIVITIES_PANEL_OPEN_EVENT`; status polling runs only while it is open.
 */
export function RightSidebar({ open: controlledOpen, onOpenChange, onActivityChange }: RightSidebarProps = {}) {
  const t = useTranslation()
  const isControlled = controlledOpen !== undefined
  const [internalOpen, setInternalOpen] = useState(false)
  const isOpen = isControlled ? controlledOpen : internalOpen

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!isControlled) {
        setInternalOpen(nextOpen)
      }
      onOpenChange?.(nextOpen)
    },
    [isControlled, onOpenChange],
  )

  const { items, ticketCreationTick } = useActivitySidebarItems({ polling: isOpen })
  const hasActivity = items.length > 0

  useEffect(() => {
    onActivityChange?.(hasActivity)
  }, [hasActivity, onActivityChange])

  const { setPollingActive } = useNexthinkActionTracker()
  // Compared against the previous tick so reloads never auto-open the panel.
  const previousTickRef = useRef(ticketCreationTick)

  useEffect(() => {
    if (!isControlled) {
      onOpenChange?.(isOpen)
    }
  }, [isControlled, isOpen, onOpenChange])

  useEffect(() => {
    setPollingActive(isOpen && hasActivity)
    return () => setPollingActive(false)
  }, [isOpen, hasActivity, setPollingActive])

  useEffect(() => {
    if (ticketCreationTick !== previousTickRef.current) {
      handleOpenChange(true)
      previousTickRef.current = ticketCreationTick
    }
  }, [ticketCreationTick, handleOpenChange])

  useEffect(() => {
    if (consumePendingActivitiesPanelOpen()) handleOpenChange(true)

    const handleOpenRequest = () => handleOpenChange(true)
    window.addEventListener(ACTIVITIES_PANEL_OPEN_EVENT, handleOpenRequest)
    return () => window.removeEventListener(ACTIVITIES_PANEL_OPEN_EVENT, handleOpenRequest)
  }, [handleOpenChange])

  if (!hasActivity) return null

  return (
    <>
      {isOpen ? (
        <div
          className={styles.rightSidebar__backdrop}
          onClick={() => handleOpenChange(false)}
          data-testid="right-sidebar-backdrop"
          aria-hidden="true"
        />
      ) : null}
      <aside
        className={`${styles.rightSidebar} ${isOpen ? styles['rightSidebar--open'] : ''}`}
        aria-label={t('chatBot.rightSidebar.panelLabel')}
      >
        <button
          type="button"
          className={`${styles.rightSidebar__header} ${isOpen ? '' : styles['rightSidebar__header--collapsed']
            }`}
          onClick={() => handleOpenChange(!isOpen)}
          aria-expanded={isOpen}
          aria-label={isOpen ? t('chatBot.rightSidebar.closePanel') : t('chatBot.rightSidebar.openPanel')}
        >
          <Icon name="arrow_right" />
        </button>
        {isOpen ? (
          <div className={styles.rightSidebar__content}>
            <div className={styles.rightSidebar__titleRow}>
              <EonUiHeadline text={t('chatBot.rightSidebar.panelLabel')} size="h5" />
              <button
                type="button"
                className={styles.rightSidebar__closeButton}
                onClick={() => handleOpenChange(false)}
                aria-label={t('chatBot.close')}
              >
                <Icon name="close" size="normal" />
              </button>
            </div>
            {items.length === 0 ? (
              <p className={styles.rightSidebar__empty}>{t('chatBot.rightSidebar.emptyState')}</p>
            ) : (
              items.map((item) =>
                item.kind === 'task' ? (
                  <TaskRow key={item.id} item={item} />
                ) : (
                  <TicketCard key={item.id} item={item} />
                ),
              )
            )}
          </div>
        ) : null}
      </aside>
    </>
  )
}
